import React, { ChangeEvent, useContext, useEffect, useMemo } from "react";
import { isNil, isNumber, isUndefined } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { CREATOR_AND_UP, GUEST, RoleName } from "common/authorization";
import { MAX_POSTGRES_COLUMN_NAME_LENGTH } from "common/constants";
import { AuthContext } from "../../../../../../Authorization/AuthContext";
import { BANNER_TYPES } from "../../../../../../Common/__styles__/Banner";
import Banner from "../../../../../../Common/Banner";
import Disclaimer from "../../../../../../Common/Disclaimer";
import { InformationalTooltip } from "../../../../../../Common/Tooltip";
import { Body, Title } from "../../../../../../Common/Typography";
import { Label } from "../../../../../../Inputs";
import { Number } from "../../../../../../Inputs";
import { Checkbox, Text } from "../../../../../../Inputs/react-hook-form";
import { TagSelect } from "../../../../../../Inputs/react-hook-form";
import Select from "../../../../../../Inputs/Select";
import {
  numberDynamicDefaultInputOptions,
  textDynamicDefaultInputOptions,
  VIEWER_TYPE,
  viewerTypeLabel,
  viewerTypeToFieldPermissionsMap,
} from "../../constants";
import { SubmissionsBuilderContextInstance } from "../../context";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import { RoleAndGroupSelector } from "../../RoleAndGroupSelector";
import {
  InputFieldPath,
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { getInputOptionFromFieldPermissions } from "../../utils";
import {
  ColumnHeaderWrapper,
  FieldWrapper,
  FormSection,
  FormSectionTitleWrapper,
} from "../__styles__/SidebarColumn";

export const FieldSettingConfig = ({
  fieldPath,
  isModuleInput,
  onRequiredChange,
  readOnly,
  isFormulaField,
  labelRequired = true,
  requiredFieldDisabled = false,
}: Pick<SelectedSubmissionsBuilderField, "fieldPath"> &
  SubmissionsBuilderFieldSettings & {
    onRequiredChange?: (evt: ChangeEvent<HTMLInputElement>) => void;
    labelRequired?: boolean;
    requiredFieldDisabled?: boolean;
    isFormulaField: boolean;
  }) => {
  const { control, register } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  return (
    <>
      {isFormulaField && (
        <Disclaimer
          message={
            "This field is derived from a formula and cannot be modified directly. You may still customize supporting text, default values, and permissions."
          }
          margin={false}
        />
      )}
      <div>
        <Label
          text={"Field label"}
          htmlFor={`${fieldPath}.title`}
          required={labelRequired}
        />
        <Text {...register(`${fieldPath}.title`)} disabled={readOnly} />
      </div>
      {!isFormulaField && (
        <Checkbox
          control={control}
          name={`${fieldPath}.required`}
          additionalOnChange={onRequiredChange}
          disabled={isModuleInput || readOnly || requiredFieldDisabled}
          label="Required field"
        />
      )}

      <div>
        <Label text={"Tooltip text"} htmlFor={`${fieldPath}.tooltipText`} />
        <Text {...register(`${fieldPath}.tooltipText`)} disabled={readOnly} />
      </div>
    </>
  );
};

export const FieldPermissionsConfig = ({
  fieldPath,
}: Pick<SelectedSubmissionsBuilderField, "fieldPath">) => {
  return (
    <FormSection>
      <FormSectionTitleWrapper>
        <Title size="small" type="semiBold">
          Default permissions
        </Title>
      </FormSectionTitleWrapper>
      <CreatorPermissionsConfig fieldPath={fieldPath} />
      <ViewerPermissionsConfig fieldPath={fieldPath} />
      <EditorPermissionsConfig fieldPath={fieldPath} />
    </FormSection>
  );
};

export const CreatorPermissionsConfig = ({
  fieldPath,
  label = "Who can fill out this field?",
}: Pick<SelectedSubmissionsBuilderField, "fieldPath"> & {
  label?: string;
}) => {
  const { control } = useFormContext<SubmissionsBuilderFormDataStructure>();
  const { account } = useContext(AuthContext);
  const userGroups = account?.userGroups ?? [];

  return (
    <FieldWrapper>
      <RoleAndGroupSelector
        control={control}
        name={`${fieldPath}.viewOnCreate`}
        label={label}
        data-testid="field-creator-permissions"
        options={{
          roles: [...CREATOR_AND_UP, ...GUEST],
          userGroups,
        }}
      />
    </FieldWrapper>
  );
};

export const ViewerPermissionsConfig = ({
  fieldPath,
}: Pick<SelectedSubmissionsBuilderField, "fieldPath">) => {
  const { account } = useContext(AuthContext);
  const { control, getFieldState } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const publicPortalEnabled = account?.publicPortal.enabled;

  const options =
    publicPortalEnabled ?
      [VIEWER_TYPE.TEAM, VIEWER_TYPE.TEAM_AND_GUESTS].map(value => ({
        value,
        label: viewerTypeLabel[value],
      }))
    : [VIEWER_TYPE.TEAM].map(value => ({
        value,
        label: viewerTypeLabel[value],
      }));

  const transform = {
    output: (value: Maybe<VIEWER_TYPE>) => {
      return value ? viewerTypeToFieldPermissionsMap[value] : [];
    },
    input: (value: Maybe<Array<RoleName>>) => {
      return getInputOptionFromFieldPermissions(
        value,
        viewerTypeToFieldPermissionsMap,
      );
    },
  };

  const error = getFieldState(`${fieldPath}.viewOnRetrieve`)?.error;

  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (error.type === "minItems") {
      return "This field is required";
    }
    return error.message;
  }, [error]);

  return (
    <FieldWrapper>
      <TagSelect
        control={control}
        name={`${fieldPath}.viewOnRetrieve`}
        options={options}
        label="Who can view this field after the form is submitted?"
        iconName="eye"
        isMulti={false}
        disabled={!publicPortalEnabled}
        transform={transform}
        required
        error={errorMessage}
        data-testid="field-viewer-permissions"
      />
    </FieldWrapper>
  );
};

export const EditorPermissionsConfig = ({
  fieldPath,
}: Pick<SelectedSubmissionsBuilderField, "fieldPath">) => {
  const { control } = useFormContext<SubmissionsBuilderFormDataStructure>();
  const { account } = useContext(AuthContext);
  const userGroups = account?.userGroups ?? [];

  //exclude "guest" from options
  const options = {
    roles: [...CREATOR_AND_UP, "publicUser" as const],
    userGroups,
  };

  return (
    <FieldWrapper>
      <RoleAndGroupSelector
        control={control}
        name={`${fieldPath}.canUpdate`}
        options={options}
        label="Who can edit this field after the form is submitted?"
        iconName="pencil"
        data-testid="field-editor-permissions"
      />
    </FieldWrapper>
  );
};

export const DefaultValuesSection = ({
  children,
  showTooltip = false,
}: {
  children: React.ReactNode;
  showTooltip?: boolean;
}) => {
  return (
    <FormSection>
      <FormSectionTitleWrapper>
        <Title size="small" type="semiBold">
          Default value
          {showTooltip && (
            <InformationalTooltip tooltipText="If both static and dynamic default values are set, the dynamic default value will be used." />
          )}
        </Title>
      </FormSectionTitleWrapper>
      {children}
    </FormSection>
  );
};

export const DisplaySettingsSection = ({
  fieldPath,
  readOnly,
  isNestedField = false,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
  isNestedField?: boolean;
}) => {
  const { register } = useFormContext<SubmissionsBuilderFormDataStructure>();
  const { fieldContextsByName } = useContext(SubmissionsBuilderContextInstance);
  const fieldContext = Object.values(fieldContextsByName).find(
    context => context.fieldPath === fieldPath,
  );

  if (!fieldContext) {
    return null;
  }

  const { titles, columnHeaderOverride } = fieldContext;
  const joinedTitles = titles.join(" > ");
  const defaultHeaderTooLong =
    joinedTitles.length >
    (isNestedField ? 30 : MAX_POSTGRES_COLUMN_NAME_LENGTH);
  const needsOverride = defaultHeaderTooLong && !columnHeaderOverride;

  return (
    <FormSection>
      <FormSectionTitleWrapper>
        <Title size="small" type="semiBold">
          Display settings
        </Title>
      </FormSectionTitleWrapper>
      <Body size="default" type="regular">
        This field will appear in tables and exported files with the header:
      </Body>
      <ColumnHeaderWrapper>
        <Body size="default" type="regular">
          {columnHeaderOverride || joinedTitles}
        </Body>
      </ColumnHeaderWrapper>
      {needsOverride && (
        <Banner type={BANNER_TYPES.ATTENTION} title="">
          <Body size="default" type="emphasis">
            The default column header is too long for exports.
          </Body>
          <Body size="default" type="regular">
            Provide an override label to ensure this field displays properly.
          </Body>
        </Banner>
      )}
      <FieldWrapper>
        <Label
          text={"Column header override label"}
          htmlFor={`${fieldPath}.columnHeaderOverride`}
          description="Use a shorter or alternate label for this field in tables and file exports."
          required={defaultHeaderTooLong}
        />
        <Text
          {...register(`${fieldPath}.columnHeaderOverride`)}
          maxLength={30}
          helperText={"Max 30 characters"}
          disabled={readOnly}
        />
      </FieldWrapper>
    </FormSection>
  );
};

export const TextDynamicDefaultInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValuePath = watch(`${fieldPath}.defaultValuePath`);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValuePath`}
      render={({ field }) => {
        return (
          <Select
            value={defaultValuePath ?? null}
            isClearable={true}
            label="Dynamic default value"
            name={`${fieldPath}.defaultValuePath`}
            options={textDynamicDefaultInputOptions}
            onChange={value => {
              field.onChange(value ?? undefined);
            }}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

export const NumberDynamicDefaultInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValuePath = watch(`${fieldPath}.defaultValuePath`);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValuePath`}
      render={({ field }) => {
        return (
          <Select
            value={defaultValuePath ?? null}
            isClearable={true}
            label="Dynamic default value"
            name={`${fieldPath}.defaultValuePath`}
            options={numberDynamicDefaultInputOptions}
            onChange={value => {
              field.onChange(value ?? undefined);
            }}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

export const NumberStaticDefaultInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, trigger, watch, getFieldState } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValue = watch(`${fieldPath}.defaultValue`);
  const minimum = watch(`${fieldPath}.minimum`);
  const maximum = watch(`${fieldPath}.maximum`);
  const error = getFieldState(`${fieldPath}.defaultValue`)?.error;

  useEffect(() => {
    // re-trigger validation when min or max boundaries change
    void trigger(`${fieldPath}.defaultValue`);
  }, [minimum, maximum, trigger, fieldPath]);

  const value = useMemo<number | null>(() => {
    if (isNumber(defaultValue) || isUndefined(defaultValue)) {
      return defaultValue ?? null;
    }
    throw new Error(`defaultValue must be a number or undefined`);
  }, [defaultValue]);

  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (error.type === "minimum" && !isNil(minimum)) {
      return `Must be greater than or equal to ${minimum}`;
    } else if (error.type === "maximum" && !isNil(maximum)) {
      return `Must be less than or equal to ${maximum}`;
    }
    return error.message;
  }, [error, minimum, maximum]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <Number
            name={`${fieldPath}.defaultValue`}
            label="Static default value"
            value={value}
            onChange={value => {
              // store no value (undefined) for ui:defaultValue instead of null in form state
              field.onChange(value ?? undefined);
            }}
            disabled={readOnly}
            readonly={readOnly}
            error={errorMessage}
          />
        );
      }}
    />
  );
};
